<template>
  <main id="main-content">
    <section v-if="submitPolicyInProcess">
      <h1>{{ submitPolicyInProcessHeader }}</h1>
      <p v-html="ErrorStrings.SUBMIT_POLICY_IN_PROCESS_ERROR.SUBHEADER"></p>
    </section>
    <PremiumImpactInformation v-else :showPremiumInformation="false" :refData="refData">
      <template #header>
        <h1>{{ referenceData.premiumImpactRefData.headingText.informationText }}</h1>
      </template>
      <template #headerInfoText>
        <p v-html="referenceData.premiumImpactRefData.informationText.informationText"></p>
      </template>
      <template #productPremiumContent>
        <ProductSummaryCard>
          <template #left>
            <h3>{{ productName }}</h3>
            <!-- <p>
              Previous premium:
              <strong>${{ productInfo.oldPremium }} /&nbsp;{{ productInfo.frequency }}</strong>
            </p> -->
            <p v-html="referenceData.premiumImpactRefData.productInformationText.informationText" />
          </template>
          <template #right>
            <h4>Your new ongoing premium</h4>
            <p class="text-green bold no-wrap">
              <span class="font-large">${{ productInfo.newPremium }}</span>
              /&nbsp;{{ productInfo.frequency }}
            </p>
          </template>
        </ProductSummaryCard>
      </template>
      <template #premiumAdjustmentTimeline>
        <ApiLoading v-if="apiStatus.loading" :apiError="apiStatus.fail" />
        <div v-else>
          <PremiumAdjustmentTimeline
            :refData="{
              paymentAdjustmentInformationText: { informationText: referenceData.premiumImpactRefData.paymentAdjustedText.informationText, title: 'Your premium payments' },
            }"
            :premiumTimelineData="premiumTimelineData"
            :isCRM="false"
            :pageTo="MY_COVER_MAKE_A_PAYMENT"
            @handlePayNowBtnClick="updateCurrentPage"
            :homePage="HOME"
            :makeAPaymentCrmBreadcrumbs="crmBreadcrumbs"
          />
        </div>
      </template>
    </PremiumImpactInformation>
  </main>
</template>

<script>
import { format } from 'date-fns';
import { PremiumAdjustmentTimeline, PremiumImpactInformation, ProductSummaryCard } from '@/components';
import PremiumImpactServices from '@/services/PremiumImpact';
import ApiLoading from '@/components/ApiLoading.vue';
import { MY_COVER_MAKE_A_PAYMENT, HOME } from '@/router/routeNames';
import { setNavigationState } from '@/router/navigationState';
import { ErrorStrings } from '@general/js/constants';
import { generateSubmitPolicyInProcessHeader } from '@/utils/DisplayUtils';
import AddRemovePersonMixins from './AddRemovePersonMixins';

export default {
  name: 'AddRemovePersonSuccessConfirmation',

  mixins: [AddRemovePersonMixins],

  props: {
    /**
     * Information about the product, including premium details.
     * This prop is only required if `submitPolicyInProcess` is false.
     */
    productInfo: {
      type: Object,
      required: false,
    },
    /**
     * Flow name to be used in generating submit policy in process header.
     * This prop is only required if `submitPolicyInProcess` is true.
     */
    flowName: {
      type: String,
      required: false,
    },
  },

  components: {
    ApiLoading,
    PremiumAdjustmentTimeline,
    ProductSummaryCard,
    PremiumImpactInformation,
  },

  data() {
    return {
      MY_COVER_MAKE_A_PAYMENT,
      HOME,
      premiumTimelineData: [
        {
          paymentAmount: null,
          label: 'Difference to amount paid',
        },
        {
          paymentAmount: null,
          label: 'Next payment',
          date: null,
        },
        {
          paymentAmount: null,
          label: 'Payment onwards',
          date: null,
        },
      ],
      apiStatus: {
        loading: false,
        fail: false,
      },
      productName: null,
      ErrorStrings,
    };
  },

  beforeMount() {
    if (!this.submitPolicyInProcess) {
      this.getPremiumAdjustmentPrices();
      this.productName = this.membershipInfo.productName;
    }
  },

  computed: {
    refData() {
      return this.referenceData?.premiumImpactRefData
        ? { premiumImpactInformationText: this.referenceData.premiumImpactRefData }
        : {};
    },

    crmBreadcrumbs() {
      return [
        {
          text: 'Add or remove person/s',
          navigate: HOME,
        },
      ];
    },

    submitPolicyInProcessHeader() {
      return generateSubmitPolicyInProcessHeader(this.flowName, this.submitPolicyInProcess);
    },
  },

  methods: {
    updateCurrentPage(amount) {
      window.scrollTo(0, 0);
      setNavigationState({ paymentAmount: amount });
      this.$router.push({ name: MY_COVER_MAKE_A_PAYMENT });
    },

    async getPremiumAdjustmentPrices() {
      this.apiStatus.loading = true;

      try {
        const currentMemberNumber = this.memberNumber;
        const currentDate = format(new Date(), 'yyyy-MM-dd');
        let showPremiumPaymentAmount = false;
        if (this.productInfo.oldPremium === null && this.productInfo.newPremium === null) {
          // only request premium payment amount from API if data was not available in CRM
          showPremiumPaymentAmount = true;
        }

        this.premiumAdjustmentPrices = await PremiumImpactServices.getPremiumAdjustmentPrices(currentMemberNumber, showPremiumPaymentAmount, currentDate);
        this.premiumTimelineData[0].paymentAmount = this.premiumAdjustmentPrices.prorataPrice;

        if (this.productInfo.newPremium !== null) {
          this.premiumTimelineData[1].paymentAmount = parseFloat(this.productInfo.newPremium);
          this.premiumTimelineData[2].paymentAmount = parseFloat(this.productInfo.newPremium);
        } else if (this.productInfo.oldPremium !== null) {
          this.premiumTimelineData[1].paymentAmount = parseFloat(this.productInfo.oldPremium);
          this.premiumTimelineData[2].paymentAmount = parseFloat(this.productInfo.oldPremium);
        } else {
          this.premiumTimelineData[1].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
          this.premiumTimelineData[2].paymentAmount = this.premiumAdjustmentPrices.standardPremiumPaymentAmount;
        }
        this.premiumTimelineData[1].date = this.premiumAdjustmentPrices.nextContributionDate;
        this.premiumTimelineData[2].date = this.premiumAdjustmentPrices.futureContributionDate;
      } catch (err) {
        logger.error(err);
        this.apiStatus.fail = true;
      } finally {
        this.apiStatus.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
